import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress, 
  Box, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../utils/api';
import SidebarAd from '../components/SidebarAd';
import CategoryPromotion from '../components/CategoryPromotion';

interface Category {
  id: number;
  name: string;
}

interface ServiceProvider {
  id: number;
  businessName: string;
  contactPerson: string;
  phone: string;
  email: string;
  address: string;
  description: string;
  services: string[];
  serviceArea: string;
  operatingHours: string;
  status: string;
  categories: Array<{
    category: {
      id: number;
      name: string;
    };
  }>;
}

const Categories: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [providersLoading, setProvidersLoading] = useState(false);
  const [error, setError] = useState('');
  const [showProvidersDialog, setShowProvidersDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        setCategories(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = async (category: Category) => {
    setSelectedCategory(category);
    setProvidersLoading(true);
    setShowProvidersDialog(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/providers/category/${category.id}`);
      if (!response.ok) throw new Error('Failed to fetch providers for this category');
      const data = await response.json();
      setProviders(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setProvidersLoading(false);
    }
  };

  // const handleBookService = (providerId: number) => {
  //   setShowProvidersDialog(false);
  //   navigate('/bookings', { state: { providerId } });
  // };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container maxWidth="lg">
      <Grid container spacing={3}>
        <Grid item xs={12} md={9}>
          <Typography variant="h4" gutterBottom>All Categories</Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            Click on a category to see available service providers
          </Typography>
      
          {categories.length === 0 ? (
            <Typography>No categories found.</Typography>
          ) : (
            <Grid container spacing={3}>
              {categories.map(category => (
                <Grid item xs={12} sm={6} md={4} key={category.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer', 
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => handleCategoryClick(category)}
                  >
                    <CardContent>
                      <Typography variant="h6" align="center">{category.name}</Typography>
                      <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                        Click to view providers
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
        
        <Grid item xs={12} md={3}>
          <SidebarAd />
        </Grid>
      </Grid>

      {/* Providers Dialog */}
      <Dialog 
        open={showProvidersDialog} 
        onClose={() => setShowProvidersDialog(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {selectedCategory?.name} - Service Providers
        </DialogTitle>
        <DialogContent>
          {providersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {selectedCategory && <CategoryPromotion categoryId={selectedCategory.id} />}
              {providers.length === 0 ? (
                <Typography color="textSecondary" align="center" sx={{ p: 3 }}>
                  No service providers found for this category.
                </Typography>
              ) : (
                <Grid container spacing={2}>
              {providers.map((provider) => (
                <Grid item xs={12} key={provider.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box flex={1}>
                          <Typography variant="h6" gutterBottom>
                            {provider.businessName}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Contact:</strong> {provider.contactPerson}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Phone:</strong> {provider.phone}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Email:</strong> {provider.email}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Address:</strong> {provider.address}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Service Area:</strong> {provider.serviceArea}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            <strong>Hours:</strong> {provider.operatingHours}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {provider.description}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              <strong>Services:</strong>
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {provider.services.map((service, index) => (
                                <Chip 
                                  key={index} 
                                  label={service} 
                                  size="small" 
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        </Box>
                        <Box>
                          <Chip 
                            label={provider.status} 
                            color={provider.status === 'approved' ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                        {/* <Button 
                          variant="contained" 
                          size="small"
                          onClick={() => handleBookService(provider.id)}
                        >
                          Book Service
                        </Button> */}
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => navigate(`/providers/${provider.id}`)}
                        >
                          View Details
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
                </Grid>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowProvidersDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Categories; 